// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Trash2Line from "@store/Library/Components/Toolkit/Icons/Trash2/Trash2Line";
import constants from "@store/constants";
import loadStoreListCustomers from "@store/Framework/Store/Data/StoreListDetails/Handlers/LoadStoreListCustomers";
import { getStoreListShipTosDataView } from "@store/Framework/Store/Data/StoreListDetails/StoreListDetailsSelector";
import deleteCustomerFromStoreList from "@store/Framework/Store/Pages/StoreListsDetails/Handlers/DeleteCustomerFromStoreList";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let id;
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ listId?: string }>(location.search);
        id = parsedQuery.listId;
    }

    return {
        listStoreDataView: getStoreListShipTosDataView(state, state.store.pages.storeListsDetails.customersParameter),
        customerParameter: state.store.pages.storeListsDetails.customersParameter,
        storeListId: id,
    };
};

const mapDispatchToProps = {
    deleteCustomerFromStoreList,
    loadStoreListCustomers,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListStyles {
    wrapper?: InjectableCss;
    actionDeleteIcon?: IconPresentationProps;
    noStoresMessage?: InjectableCss;
}

export const productListStyles: MyListsDetailsProductListStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    actionDeleteIcon: {
        size: 22,
        css: css`
            color: ${getColor("icon")};
        `,
    },
    noStoresMessage: {
        css: css`
            margin-top: ${pxToRem(20)};
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
};

const styles = productListStyles;

const StoreListsDetailsStoreList: React.FC<Props> = ({
    listStoreDataView: baseDataView,
    storeListId,
    customerParameter,
    deleteCustomerFromStoreList,
    loadStoreListCustomers,
}) => {
    const listStoreDataView = usePersistentDataView(baseDataView);
    useEffect(() => {
        if (customerParameter.storeListId) {
            loadStoreListCustomers(customerParameter);
        }
    }, [customerParameter]);

    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
    const [customerIdToDelete, setCustomerIdToDelete] = useState("");

    const deleteStoreListCustomerClickHandler = (customerId: string) => {
        setDeleteModalIsOpen(true);
        setCustomerIdToDelete(customerId);
    };

    const deleteCancelHandler = () => {
        setDeleteModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        setDeleteModalIsOpen(false);
        if (customerIdToDelete.length > 0 && storeListId) {
            deleteCustomerFromStoreList({
                storeListId,
                storeListCustomerId: customerIdToDelete,
            });
        }
    };

    if (listStoreDataView.isLoading) {
        return (
            <StyledWrapper>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!listStoreDataView.value || listStoreDataView.value?.length === 0) {
        return (
            <StyledWrapper {...styles.noStoresMessage}>{translate("Start adding stores to your list")}</StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader></DataTableHeader>
                    <DataTableHeader>{translate("Id")}</DataTableHeader>
                    <DataTableHeader>{translate("Customer")}</DataTableHeader>
                    <DataTableHeader>{translate("Address")}</DataTableHeader>
                    <DataTableHeader>{translate("City")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {listStoreDataView.value?.map(shipTo => (
                        <DataTableRow key={shipTo.id}>
                            <DataTableCell>
                                <Link
                                    onClick={(event: React.MouseEvent) => {
                                        deleteStoreListCustomerClickHandler(shipTo.id);
                                    }}
                                >
                                    <Icon {...styles.actionDeleteIcon} src={Trash2Line} />
                                </Link>
                            </DataTableCell>
                            <DataTableCell>{shipTo.customerSequence}</DataTableCell>
                            <DataTableCell>{shipTo.customerName}</DataTableCell>
                            <DataTableCell>{shipTo.address}</DataTableCell>
                            <DataTableCell>{shipTo.city}</DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
            <TwoButtonModal
                modalIsOpen={deleteModalIsOpen}
                headlineText={translate("Delete Store from Store List")}
                messageText={translate("Are you sure you want to delete this Store?")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="storeListSubmitDelete"
            ></TwoButtonModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(StoreListsDetailsStoreList),
    definition: {
        group: constants.widgetGroup.storeListDetails,
        displayName: "Store List Details Store List",
    },
};

export default widgetModule;
